import { useState } from "react";
import { useApi } from "@/api/useApi";
import type { ExpensePolicy } from "@/api/types";
import { relativeTime } from "@/format";

type Result = { ok: boolean; text: string } | undefined;

interface Refreshed {
  checked: number;
  updated: number;
  unanswered: number;
  error: string | null;
}

/**
 * Brings everyone's manager, department, employee ID and job title up to date from Entra now,
 * rather than waiting for 5am or for them to sign in.
 */
export function DirectoryRefreshCard({
  policy,
  onRefreshed,
}: {
  policy: ExpensePolicy;
  onRefreshed: () => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>();

  function refresh() {
    setBusy(true);
    setResult(undefined);
    api
      .post<Refreshed>("/api/expenses/settings/directory/refresh")
      .then((done) => {
        const changed =
          done.updated === 0
            ? "Everyone was already up to date."
            : `Updated ${String(done.updated)} ${done.updated === 1 ? "person" : "people"}.`;
        setResult({
          ok: done.unanswered === 0,
          text: `Checked ${String(done.checked)} people. ${changed}${done.error ? ` ${done.error}` : ""}`,
        });
        onRefreshed();
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  return (
    <section className="card stack">
      <h2>People from Entra</h2>
      <p className="muted">
        Each person&apos;s manager, department, employee ID and job title are copied from Entra when
        they sign in, and for everyone at 5am each day. After changing someone in Entra, refresh
        here so their reports show it straight away.
      </p>
      <dl className="facts">
        <div>
          <dt>Last refreshed</dt>
          <dd>
            {policy.directoryRefreshedAt ? relativeTime(policy.directoryRefreshedAt) : "Never"}
          </dd>
        </div>
        {policy.directoryRefreshError && (
          <div>
            <dt>Last attempt</dt>
            <dd className="danger-text">{policy.directoryRefreshError}</dd>
          </div>
        )}
      </dl>
      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
      <button type="button" className="btn secondary" disabled={busy} onClick={refresh}>
        {busy ? "Refreshing…" : "Refresh from Entra now"}
      </button>
    </section>
  );
}
